import React from "react";
import { Link } from "react-router-dom";
import { Nav } from "@/components/layout/Nav";
import { Footer } from "@/components/layout/Footer";
import { PageSEO } from "@/components/seo/PageSEO";
import { SITE_URL } from "@/utils/seo";
import { ArrowRight, Users, CheckCircle } from "lucide-react";

const SITE = SITE_URL;

const doubles = [
  { slug: "baby-jogger-city-mini-gt2-double", name: "Baby Jogger City Mini GT2 Double", price: 34, highlight: "One-hand fold, all-terrain wheels, fits two kids up to 65 lbs each." },
  { slug: "bob-double-stroller", name: "BOB Double Stroller", price: 36, highlight: "Rugged suspension, ideal for active families. Fits two children up to 50 lbs each." },
  { slug: "baby-jogger-city-mini-double", name: "Baby Jogger City Mini Double", price: 30, highlight: "Lightweight side-by-side, compact fold, all-terrain ready." },
  { slug: "zoe-v2-twin", name: "Zoe V2 Twin", price: 30, highlight: "Lightweight and travel-friendly, great for longer park days." },
  { slug: "zoe-v1-twin", name: "Zoe V1 Twin", price: 28, highlight: "Budget-friendly double, light enough for Disney buses and Skyliner." },
  { slug: "baby-jogger-gt-double", name: "Baby Jogger GT Double", price: 32, highlight: "All-terrain performance with all-weather canopies." },
];

const faqs = [
  {
    q: "Do double strollers meet Disney World's size rules?",
    a: "Yes. All of our double strollers are within Disney's required limit of 31 inches wide and 52 inches long. You will need to fold the stroller to board Disney buses, the Skyliner and the Monorail.",
  },
  {
    q: "What is the best double stroller for Disney World?",
    a: "The Baby Jogger City Mini GT2 Double is our most popular double. It folds with one hand, handles all terrain and fits two children up to 65 lbs each. The BOB Double is ideal for families who want maximum suspension for a longer trip.",
  },
  {
    q: "Can a double stroller fit two children at different ages?",
    a: "Yes. Side-by-side doubles like ours accommodate children of different ages and sizes. Check the per-seat weight limits for each model — our product pages list verified specs.",
  },
  {
    q: "Is a double stroller worth it for Disney if only one child needs it?",
    a: "Many families rent a double stroller even with one young child, because an older child who tires midday can ride instead of asking to be carried. It also gives you extra storage. Our stroller quiz can help you decide.",
  },
  {
    q: "Is delivery free for double strollers?",
    a: "Yes. Delivery is free for all stroller models to Disney resorts, Universal Orlando, hotels and vacation homes throughout the Orlando area.",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
    { "@type": "ListItem", position: 2, name: "Strollers", item: `${SITE}/strollers` },
    { "@type": "ListItem", position: 3, name: "Double Stroller Rentals Orlando", item: `${SITE}/double-stroller-rentals-orlando` },
  ],
};

export default function DoubleStrollerRentals() {
  return (
    <>
      <PageSEO
        title="Double Stroller Rentals Orlando & Disney World | Enchanted Gear Rentals"
        description="Rent a double stroller for your Orlando or Disney World vacation. Baby Jogger GT2 Double, BOB Double, Zoe Twin and more — free delivery to your resort or hotel from $28/day."
        canonical={`${SITE}/double-stroller-rentals-orlando`}
        structuredData={[breadcrumbSchema, faqSchema]}
      />
      <Nav />
      <main className="min-h-screen" style={{ background: "#040A0F" }}>
        <section className="pt-28 pb-16 text-center" style={{ background: "linear-gradient(180deg, #060f18 0%, #040A0F 100%)" }}>
          <div className="container max-w-3xl">
            <nav aria-label="Breadcrumb" className="text-xs text-[#B9C8D5] mb-4">
              <Link to="/" className="hover:text-[#FFD166]">Home</Link>
              <span className="mx-2 opacity-40">/</span>
              <Link to="/strollers" className="hover:text-[#FFD166]">Strollers</Link>
              <span className="mx-2 opacity-40">/</span>
              <span className="text-[#FFD166]">Double Stroller Rentals</span>
            </nav>
            <div className="flex justify-center mb-4"><Users className="w-10 h-10 text-[#FFD166]" /></div>
            <h1 className="font-serif text-4xl md:text-5xl text-[#F6F1E4] mb-5">Double Stroller Rentals Near Disney World</h1>
            <p className="text-[#B9C8D5] text-lg max-w-2xl mx-auto">
              Side-by-side double strollers from top brands, delivered free to your Walt Disney World resort or Orlando-area hotel. All models meet Disney's size requirements and fold for park transportation.
            </p>
            <div className="mt-7 flex gap-4 justify-center flex-wrap">
              <Link to="/#trip-setup"><button className="btn-primary" data-testid="double-cta-top">Check Availability <ArrowRight className="inline w-4 h-4 ml-1" /></button></Link>
              <Link to="/single-stroller-rentals-orlando"><button className="btn-ghost">Need a single stroller?</button></Link>
            </div>
          </div>
        </section>

        {/* Why double */}
        <section className="container py-12">
          <h2 className="font-serif text-3xl text-[#F6F1E4] mb-6">When to rent a double stroller for Disney World</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="rounded-xl bg-[#0A1825] border border-[#FFD166]/15 p-6">
              <h3 className="font-serif text-lg text-[#FFD166] mb-3">Good fit for a double</h3>
              <ul className="space-y-2">
                {[
                  "Two children under stroller age (typically 5 and under)",
                  "An older child (ages 5–8) who tires quickly at parks",
                  "Long days where both kids need nap time in the stroller",
                  "Families who want extra cargo space for bags and snacks",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2 text-sm text-[#F6F1E4]">
                    <CheckCircle className="w-4 h-4 text-[#FFD166] shrink-0 mt-0.5" /> {t}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-xl bg-[#0A1825] border border-[#FFD166]/15 p-6">
              <h3 className="font-serif text-lg text-[#FFD166] mb-3">Consider a single instead</h3>
              <ul className="space-y-2">
                {[
                  "One child who is still in a stroller",
                  "Traveling with a newborn who will mostly be worn or carried",
                  "Older children who are confident walkers for most of the day",
                  "Looking for the lightest, most compact option on buses",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2 text-sm text-[#B9C8D5]">
                    <span className="text-[#FFD166] mt-0.5 shrink-0">›</span> {t}
                  </li>
                ))}
              </ul>
              <Link to="/single-stroller-rentals-orlando" className="inline-block mt-3 text-xs text-[#FFD166] underline">Browse single strollers</Link>
            </div>
          </div>
        </section>

        {/* Products */}
        <section className="container py-10">
          <h2 className="font-serif text-3xl text-[#F6F1E4] mb-8">Our double stroller lineup</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {doubles.map((s) => (
              <Link key={s.slug} to={`/strollers/${s.slug}`} className="group rounded-xl bg-[#0A1825] border border-[#FFD166]/10 hover:border-[#FFD166]/40 p-5 transition-all hover:-translate-y-1">
                <div className="text-[10px] uppercase tracking-widest text-[#B9C8D5] border border-[#FFD166]/20 px-2 py-0.5 rounded-full w-fit mb-3">Double</div>
                <div className="font-serif text-lg text-[#F6F1E4] group-hover:text-[#FFD166] transition-colors mb-1">{s.name}</div>
                <div className="text-sm text-[#B9C8D5] mb-3">{s.highlight}</div>
                <div className="text-[#FFD166] font-semibold">From ${s.price}/day</div>
                <div className="text-xs text-[#B9C8D5] mt-1 flex items-center gap-1">Full specs &amp; photos <ArrowRight className="w-3 h-3" /></div>
              </Link>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="container py-10">
          <h2 className="font-serif text-3xl text-[#F6F1E4] mb-8">Double stroller rental FAQ</h2>
          <div className="space-y-4 max-w-3xl">
            {faqs.map((f) => (
              <details key={f.q} className="group rounded-xl bg-[#0A1825] border border-[#FFD166]/10 p-5">
                <summary className="font-semibold text-[#F6F1E4] cursor-pointer list-none flex justify-between items-center gap-3">
                  {f.q}
                  <span className="text-[#FFD166] text-xl leading-none shrink-0 group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-[#B9C8D5] text-sm leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="container pb-20 text-center">
          <div className="max-w-xl mx-auto rounded-2xl bg-[#0A1825] border border-[#FFD166]/20 p-8">
            <h2 className="font-serif text-2xl text-[#F6F1E4] mb-3">Reserve your double stroller</h2>
            <p className="text-[#B9C8D5] mb-6">Select your dates and we will show you availability across all double stroller models.</p>
            <Link to="/#trip-setup"><button className="btn-primary" data-testid="double-cta-bottom">Check Availability <ArrowRight className="inline w-4 h-4 ml-1" /></button></Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
